import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

// Actions
import { getCampaignPlan } from "../../../actions/campaign-plan/CampaignPlanActions";
import { getBasicCampaignDetails } from "../../../actions/campaign/CampaignBasicInfoActions";
import { getRoadStretchesByCityId } from "../../actions/campaign-planning/CampaignPlanningRoadStretchesActions";
import { getCampaignPerformanceByCityId } from "../../actions/campaign-performance/CampaignPerformanceActions";

// Utils and constants
import { useGetTgInfo } from "../../../mavin/utils/hooks/HooksUtil";

// Components
import Spinner from "../../../components/spinner/Spinner";
import CampaignSidebar from "../../../components/campaign-sidebar/CampaignSidebar";
import CityViewCards from "../components/CityViewCards";
import { CampaignDetails } from "../components/HeaderNav";
import MapView from "../components/map-view/MapView";
import MediaDetailsTable from "../components/MediaDetailsTable";
import PageHeader from "../../../mavin/components/page-header/PageHeader";

/**
 * Page - Campaign Complete
 */
export default function CampaignCompletePage() {
  const dispatch = useDispatch();
  const { campaignId, cityId } = useParams();

  // Get campaignPlan
  const campaignPlan = useSelector((state) => state.campaignPlan.campaignPlan);
  const { targetGroupId, isDigital } = campaignPlan || {};

  // Get campaign
  const campaign = useSelector((state) => state.c_campaignBasicInfo.campaign);

  // Campaign Media
  const campaignMedia = useSelector(
    (state) => state.campaignMedia.campaignMedia
  );

  // live ots
  const campaignMediaOtsLogSummary = useSelector(
    (state) => state.campaignPerformance.campaignMediaOtsLogSummary
  );

  // City road stretches
  const roadStretchMap = useSelector(
    (state) => state.planningRoadStretches.roadStretchDetailsMap
  );

  // Loadings
  const campaignPlanLoading = useSelector(
    (state) => state.campaignPlan.campaignPlanLoading
  );
  const basicCampaignDetailsLoading = useSelector(
    (state) => state.c_campaignBasicInfo.campaignLoading
  );
  const roadStretchesByCityIdLoading = useSelector(
    (state) => state.planningRoadStretches.loading
  );
  const tgInfoLoading = useSelector(
    (state) => state.orgTargetGroup.tgInfoLoading
  );
  const campaignCityPerformanceLoading = useSelector(
    (state) => state.campaignPerformance.campaignCityPerformanceLoading
  );

  const isPageLoading =
    campaignPlanLoading ||
    basicCampaignDetailsLoading ||
    campaignCityPerformanceLoading ||
    tgInfoLoading ||
    roadStretchesByCityIdLoading;

  useEffect(() => {
    dispatch(getCampaignPlan(campaignId, cityId));
    dispatch(getBasicCampaignDetails(campaignId));
    dispatch(getCampaignPerformanceByCityId(campaignId, cityId));
  }, [dispatch, campaignId, cityId]);

  // road stretches
  useEffect(() => {
    if (Object.keys(campaignPlan).length > 0) {
      dispatch(getRoadStretchesByCityId(cityId, "", isDigital));
    }
  }, [dispatch, JSON.stringify(campaignPlan)]);

  // tg info
  useGetTgInfo(targetGroupId);

  // Sidebar Settings
  const citySelected = { cityId };
  const redirectLinkObj = {
    campaignOverviewLink: `/campaign/${campaignId}/complete`,
    citySummaryLink: `/campaign/${campaignId}/city/:cityId/complete`,
  };

  // Page Title
  const pageTitle = <CampaignDetails campaign={campaign} />;

  // page loading
  if (isPageLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <>
      {/* SideBar */}
      <CampaignSidebar
        campaign={campaign}
        redirectLinkObj={redirectLinkObj}
        citySelected={citySelected}
      />

      <div className="content-wrapper">
        <PageHeader title={pageTitle} shadow={true} />

        {/* Page-Content */}
        <div className="page-content">
          <CityViewCards
            campaign={campaign}
            cityPlan={campaignPlan}
            campaignMedia={campaignMedia}
          />

          {/* Map View */}
          <MapView
            campaignPlan={campaignPlan}
            roadStretchMap={roadStretchMap}
          />

          {/* Media Sites Table */}
          <MediaDetailsTable
            campaignId={campaignId}
            campaign={campaign}
            campaignPlan={campaignPlan}
            campaignMediaOtsLogSummary={campaignMediaOtsLogSummary}
            roadStretchMap={roadStretchMap}
          />
        </div>
      </div>
    </>
  );
}
